import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { X, ArrowLeft, Star, Heart, Minus, Plus } from 'lucide-react';
import { formatCurrencyNPR } from '../utils/currency';
import { getProductPrice } from '../utils/productUtils';

interface MobileProductDetailProps {
  product: any;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: () => void;
  onBuyNow: () => void;
  onWishlistToggle: () => void;
  isInWishlist: boolean;
}

const MobileProductDetail: React.FC<MobileProductDetailProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  onBuyNow,
  onWishlistToggle,
  isInWishlist
}) => {
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const backdropRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Animate drawer
  useEffect(() => {
    if (isOpen) {
      if (backdropRef.current) {
        gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      }
      if (drawerRef.current) {
        gsap.fromTo(
          drawerRef.current,
          { y: '100%', opacity: 0 },
          { y: 0, opacity: 1, duration: 0.3, ease: "back.out(1.7)" }
        );
      }
    } else {
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
      }
      if (drawerRef.current) {
        gsap.to(drawerRef.current, { y: '100%', opacity: 0, duration: 0.3, ease: "power2.in" });
      }
    }
  }, [isOpen]);

  const colors = ['White', 'Black', 'Yellow', 'Light Pink'];
  const images = product.images || ['/placeholder-product.jpg'];
  const price = getProductPrice(product);
  // const originalPrice = getProductMRP(product); // Reserved for future use

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 bg-gray-900/80 z-[9997] flex items-end sm:items-center justify-center"
    >
      <div
        ref={drawerRef}
        className="bg-white w-full h-[90vh] sm:h-auto sm:max-h-[90vh] sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
      >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-sm font-medium text-gray-900 truncate flex-1 px-2">
                {product.title}
              </h1>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Image Gallery */}
              <div className="relative">
                <div className="aspect-square bg-gray-50">
                  <img
                    src={images[selectedImage]}
                    srcSet={`${images[selectedImage]} 320w, ${images[selectedImage]} 400w, ${images[selectedImage]} 600w`}
                    sizes="100vw"
                    alt={product.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width={400}
                    height={400}
                  />
                </div>
                
                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex gap-2 overflow-x-auto">
                      {images.map((image: string, index: number) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                            selectedImage === index ? 'border-primary-500' : 'border-gray-200'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${product.title} ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width={64}
                            height={64}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4 space-y-4">
                {/* Title */}
                <h2 className="text-lg font-semibold text-gray-900">
                  {product.title}
                </h2>

                {/* Rating & Reviews */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-gray-300" />
                    ))}
                  </div>
                  <span>(0) 0 Reviews | 1 Orders | 0 Wish Listed</span>
                </div>

                {/* Price */}
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrencyNPR(price)}
                </div>

                {/* Color Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Color:</label>
                  <div className="flex gap-3">
                    {colors.map((color, index) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(index)}
                        className={`w-8 h-8 rounded-full border-2 transition-colors ${
                          selectedColor === index ? 'border-primary-500' : 'border-gray-300'
                        }`}
                        style={{
                          backgroundColor: color.toLowerCase().replace(' ', '')
                        }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Size Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Size:</label>
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">
                    FREESIZE
                  </button>
                </div>

                {/* Quantity */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Quantity:</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      className="p-2 bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-medium min-w-[2rem] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= 10}
                      className="p-2 bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Total Price */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    Total Price: {formatCurrencyNPR(price * quantity)} (Tax: incl.)
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-3">
                <button
                  onClick={onBuyNow}
                  className="flex-1 bg-primary-500 text-white py-3 px-4 rounded-lg font-medium text-sm"
                >
                  Buy now
                </button>
                <button
                  onClick={onAddToCart}
                  className="flex-1 bg-black text-white py-3 px-4 rounded-lg font-medium text-sm"
                >
                  Add to cart
                </button>
                <button
                  onClick={onWishlistToggle}
                  className="p-3 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="relative">
                    <Heart className={`w-5 h-5 ${isInWishlist ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                    <span className="absolute -top-2 -right-2 bg-gray-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      0
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
  );
};

export default MobileProductDetail;
